<script setup="">
import { AlertCircleIcon, CheckCircleIcon, HeadsetIcon, LockIcon, MilestoneIcon, UserIcon } from "lucide-vue-next";
import { computed, inject, ref } from "vue";

import DarkMode from "@/Layouts/Partials/DarkMode.vue";
import WebPush from "@/Layouts/Partials/WebPush.vue";

import { Popover, PopoverButton, PopoverOverlay, PopoverPanel } from "@headlessui/vue";
import { Link, usePage } from "@inertiajs/vue3";

const emit = defineEmits(["update:open"]);

const props = defineProps({
  panelClass: {
    type: String,
    required: false
  },
  panelContentClass: {
    type: String,
    required: false
  }
});

const page = usePage();

const isOpen = ref(false);

const user = computed(() => page.props.user);

const twMerge = inject("twMerge");
const computedPanelClass = twMerge("absolute fixed z-40 overflow-hidden rounded-xl bg-white dark:bg-deep-900 shadow-soft", props.panelClass);
const computedPanelContentClass = twMerge("w-64 mx-auto", props.panelContentClass);

const navs = [
  {
    name: "계정",
    description: "계정 정보를 관리합니다.",
    icon: UserIcon,
    href: route("accounts.show"),
    current: route().current("accounts.show")
  },
  {
    name: "보안",
    description: "보안 설정을 관리합니다.",
    icon: LockIcon,
    href: route("accounts.security.index"),
    current: route().current("accounts.security.index")
  }
];

const updateOpen = (open) => {
  isOpen.value = !open;
  emit("update:open", !open);
};
</script>

<template>
  <Popover v-slot="{ open }">
    <div>
      <PopoverButton class="relative block w-full outline-none hover:text-primary focus:ring-0 dark:hover:text-primary" @click="updateOpen(open)">
        <slot :user="user" />
      </PopoverButton>
    </div>

    <teleport to="body">
      <PopoverOverlay class="fixed inset-0 z-30 bg-black opacity-30 sm:hidden" />
    </teleport>

    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-1 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-1 opacity-0"
    >
      <PopoverPanel :class="computedPanelClass">
        <div :class="computedPanelContentClass">
          <div class="flex items-center justify-between rounded-t-lg px-4 py-5">
            <div class="flex items-center space-x-4">
              <div class="avatar h-12 w-12">
                <img :src="user.profile_photo_url" alt="avatar" class="rounded-full p-px ring-2" />
              </div>
              <div>
                <a class="font-semibold">
                  {{ user.name }}
                </a>
                <p class="-mt-0.5 text-xs+ text-500">
                  {{ user.email }}
                </p>
              </div>
            </div>
            <div v-if="user.is_verified">
              <Link :href="route('accounts.show')" class="flex flex-col items-center">
                <CheckCircleIcon class="h-5 w-5 text-green-600" />
                <span class="mt-1 text-xs font-bold text-400 md:hidden">인증</span>
              </Link>
            </div>
            <div v-else>
              <Link :href="route('accounts.show')" class="flex flex-col items-center">
                <AlertCircleIcon class="h-5 w-5 text-warning" />
                <span class="mt-1 text-xs font-bold text-400">미인증</span>
              </Link>
            </div>
          </div>
          <!--end::profile-->

          <div class="flex flex-col border-t">
            <div class="space-y-1">
              <div class="grid grid-cols-4 px-4 py-2">
                <div class="text-center">
                  <div>
                    <DarkMode />
                  </div>
                  <div>
                    <span class="text-xs text-500">외관</span>
                  </div>
                </div>
                <div class="text-center">
                  <div>
                    <WebPush />
                  </div>
                  <div>
                    <span class="text-xs text-500">웹푸시</span>
                  </div>
                </div>
                <div class="text-center">
                  <a href="https://pf.kakao.com/_aLvxlG/chat" target="_blank" class="flex-center h-8">
                    <HeadsetIcon class="h-5 w-5" />
                  </a>
                  <div>
                    <span class="text-xs text-500">고객센터</span>
                  </div>
                </div>
                <div class="text-center">
                  <Link :href="route('notices.index')" class="flex-center h-8">
                    <MilestoneIcon class="h-5 w-5" />
                  </Link>
                  <div>
                    <span class="text-xs text-500">공지사항</span>
                  </div>
                </div>
              </div>

              <div class="border-y px-px py-4">
                <Link
                  v-for="(nav, index) in navs"
                  :key="index"
                  :href="nav.href"
                  class="group flex items-start space-x-3 px-4 py-3 tracking-wide outline-none transition-all text-900 hover:bg-100 dark:hover:bg-zinc-800"
                >
                  <div class="flex-center h-8 w-8">
                    <component :is="nav.icon" class="h-6 w-6" />
                  </div>

                  <div>
                    <h5 v-text="nav.name"></h5>
                    <div class="line-clamp-1 text-xs text-400" v-text="nav.description"></div>
                  </div>
                </Link>
                <!--end::menu-->
              </div>
            </div>
          </div>
        </div>
      </PopoverPanel>
    </transition>
  </Popover>
</template>
